import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import ErrorBoundary from 'src/components/ErrorBoundary/ErrorBoundary'
import Loading from 'src/components/Loading/Loading'
import SearchInput from 'src/components/SearchInput/SearchInput'
import Table from 'src/components/Table/Table'
import { useAssetUsers } from 'src/hooks/api'
import { filterUsers } from 'src/util/filters'
import AccessCell from './AccessCell'

import './AssetUsers.sass'

interface UserAssetsProps {
  slug: string
  value: string[]
  onChange: (ids: string[], isInitial?: boolean) => void
}

const AssetUsers = ({ slug, value, onChange }: UserAssetsProps) => {
  const assetUsers = useAssetUsers(slug)

  const [filter, setFilter] = useState('')
  const filteredUsers = useMemo(() => assetUsers.filter(filterUsers(filter)), [assetUsers, filter])

  const access = useMemo(() => new Set(value), [value])

  const forced = useMemo(() => new Set(
    assetUsers
      .filter(u => u.turnUpAccess)
      .map(u => u.userId)
  ), [assetUsers])

  const toggle = (id: string) => {
    if (forced.has(id)) return // Can't change these
    const clone = new Set(value)
    clone.has(id) ? clone.delete(id) : clone.add(id)
    onChange(Array.from(clone))
  }

  useEffect(() => {
    // Initialise outside's value on initial load
    onChange(assetUsers.filter(a => a.access).map(a => a.userId), true)
  }, [assetUsers])

  return (
    <section id="asset-users">
      <SearchInput value={filter} onChange={setFilter} />

      <Table
        name={'users'}
        data={filteredUsers}
        columns={[
          { name: 'name' },
          {
            name: 'access',
            component: AccessCell,
            enabled: access,
            onChange: toggle,
            forceEnabled: forced,
            idField: 'userId'
          }
        ]}
      />
    </section>
  )
}

const AssetUsersWrapper = (props) =>
  <ErrorBoundary fallback={<FormattedMessage id="error.permissions" />}>
    <Suspense fallback={<Loading />}>
      <AssetUsers {...props} />
    </Suspense>
  </ErrorBoundary>

export default AssetUsersWrapper
